import { Box } from "@mui/material"
import { ReactElement } from "react"

import BottomNavigationBar from "../components/navigation/mobileNav/bottomNavigation"
import DisplayIf from "../components/wrappers/displayIf"
import { useScreenSize } from "../hooks/useScreenSize"
import { BORDER_RADIUS } from "../themes/defaultThemeParams"

type ChildrenProps = {
    children: ReactElement
}

/**
 * Root-level webpage frame for application, defining the webpage screen.
 * All application content should sit beneath this frame in the component
 * tree. The top box is the view port. The child box is the app window
 * (all app content lives within the window)
 *
 * @example
 * <RootFrame>
 *    <AllAppContent />
 * </RootFrame>
 */

export const RootFrame = ({ children }: ChildrenProps): ReactElement => {
    const { isTabletOrMob } = useScreenSize()
    return (
        <Box
            display="flex"
            justifyContent="center"
            minHeight="100vh"
            sx={{
                backgroundColor: "background.frameBackground",
            }}
        >
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                maxWidth="2000px"
                width="100%"
                sx={{
                    overflowY: "scroll",
                    overflowX: "hidden", // No x scrolling within the app window
                    pb: isTabletOrMob ? 5 : null,
                }}
            >
                {children}
            </Box>
            <DisplayIf condition={isTabletOrMob}>
                <BottomNavigationBar />
            </DisplayIf>
        </Box>
    )
}

/**
 * Function to determine the padding for the content frame. This is reused
 * in the public homepage component to ensure the background color extends
 * to the edge of the screen, and hence is defined here.
 */
export const getContentFramePadding = (isMob: boolean): number =>
    isMob ? 1 : 3

/**
 * Frame for all webpage content, excluding navigation frame and app bar.
 * All content is rendered dynamically within this box.
 *
 * @example
 * <ContentBox>
 *    <AppContent />
 * </ContentBox>
 */
export const ContentBox = ({ children }: ChildrenProps): ReactElement => {
    const { isMob } = useScreenSize()

    return (
        <Box
            borderRadius={BORDER_RADIUS}
            flexGrow={1}
            width="100%"
            paddingX={getContentFramePadding(isMob)}
            paddingY={3}
            marginRight={3}
            sx={{
                backgroundColor: "background.contentBackground",
                overflowY: "visible",
            }}
        >
            {children}
        </Box>
    )
}
